import { useState } from 'react';
import { Search, BookOpen, GraduationCap, Award, Globe, FileCheck, Building, Mail } from 'lucide-react';
import Footer from '../components/common/Footer';

export default function KennedyUniversityPhD() {
  const [activeTab, setActiveTab] = useState('overview');

  const specializationAreas = [
    { category: "Engineering", fields: ["Biomedical Engineering"] },
    { category: "Psychology", fields: ["Clinical Psychology"] },
    { category: "Health Sciences", fields: ["Environmental Science"] },
    { category: "Social Sciences", fields: ["Sociology", "Political Science"] },
    { category: "Humanities", fields: ["Linguistics"] },
    { category: "Business Administration", fields: ["Economics"] },
    { category: "Sciences", fields: ["Chemistry", "Computer Science", "Physics", "Mathematics and Statistics", "Biological Sciences"] },
    { category: "Education", fields: ["Education", "Public Policy"] }
  ];

  const recognitions = [
    { name: "Florida Department of Education (USA)" },
    { name: "Ministry of Education, Culture & Higher Education" },
    { name: "French Ministry of National Education" },
    { name: "Ministry of Education & Higher Education" },
    { name: "European Council of Leading Business Schools" },
    { name: "International Association for Quality Assurance in Higher Education" },
    { name: "United Nations Department of Economic and Social Affairs" },
    { name: "Office of The Minister of Education, African Diaspora" },
    { name: "Quality Education Assurance Accreditation" }
  ];

  const documents = [
    "Admission Letter", 
    "Topic Approval Letter", 
    "Guide Approval Letter", 
    "Notice of Viva Voce",
    "Academic Transcript",
    "Notification of Successful Viva Voce",
    "Research Completion Letter",
    "Verification Letter"
  ];

  return (
    <div className="min-h-screen bg-richblue-5">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-richblue-200 to-richblue-100 text-richblue-700 py-12">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl font-bold mb-4">Doctor of Philosophy (Ph.D.)</h1>
          <p className="text-xl text-richblue-500">Advance your career with our research-based doctoral programs</p>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-richblue-50 border-b border-richblue-300 shadow-sm sticky top-0 z-10">
        <div className="container mx-auto px-4">
          <div className="flex overflow-x-auto py-2 space-x-8">
            <button 
              onClick={() => setActiveTab('overview')}
              className={`px-1 py-2 font-medium whitespace-nowrap ${activeTab === 'overview' ? 'text-richblue-600 border-b-2 border-richblue-600' : 'text-richblue-400 hover:text-richblue-600'}`}
            >
              Overview
            </button>
            <button 
              onClick={() => setActiveTab('specializations')}
              className={`px-1 py-2 font-medium whitespace-nowrap ${activeTab === 'specializations' ? 'text-richblue-600 border-b-2 border-richblue-600' : 'text-richblue-400 hover:text-richblue-600'}`}
            >
              Specializations
            </button>
            <button 
              onClick={() => setActiveTab('admissions')}
              className={`px-1 py-2 font-medium whitespace-nowrap ${activeTab === 'admissions' ? 'text-richblue-600 border-b-2 border-richblue-600' : 'text-richblue-400 hover:text-richblue-600'}`}
            >
              Admissions
            </button>
            <button 
              onClick={() => setActiveTab('recognition')}
              className={`px-1 py-2 font-medium whitespace-nowrap ${activeTab === 'recognition' ? 'text-richblue-600 border-b-2 border-richblue-600' : 'text-richblue-400 hover:text-richblue-600'}`}
            >
              Recognition
            </button>
           
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-12">
        {/* Overview Section */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="md:col-span-2 space-y-8">
              <div>
            <h2 className="text-3xl font-bold text-richblue-700 mb-4">Program Overview</h2>
            <p className="text-lg text-richblue-500 mb-4">
                  Kennedy University offers a wide range of online PhD programs that can be completed from anywhere in the world. 
                  These programs provide rigorous academic training and emphasize significant research contributions across various disciplines.
                </p>
            <p className="text-lg text-richblue-500 mb-4">
                  The flexible online format allows students global access to coursework and resources, making it ideal for 
                  working professionals and international students.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-richblue-5 p-6 rounded-lg shadow-md border border-richblue-300">
                  <div className="bg-richblue-100 p-3 rounded-full w-fit mb-4">
                    <BookOpen className="h-8 w-8 text-richblue-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-richblue-600 mb-2">Research-Focused</h3>
                  <p className="text-richblue-500">
                    Our PhD programs emphasize significant research contributions that advance knowledge in your field.
                  </p>
                </div>
                <div className="bg-richblue-5 p-6 rounded-lg shadow-md border border-richblue-300">
                  <div className="bg-richblue-100 p-3 rounded-full w-fit mb-4">
                    <Globe className="h-8 w-8 text-richblue-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-richblue-600 mb-2">Global Access</h3>
                  <p className="text-richblue-500">
                    Complete your doctorate from anywhere in the world with our flexible online programs.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-richblue-5 p-6 rounded-lg shadow-md border border-richblue-300">
                <h3 className="text-xl font-semibold mb-4 text-richblue-600">Apply Today</h3>
                <form className="space-y-4">
                  <div>
                    <label className="block text-richblue-500 mb-1">Full Name</label>
                    <input type="text" className="w-full p-2 rounded-md bg-richblue-100 text-richblue-900 border border-richblue-300" />
                  </div>
                  <div>
                    <label className="block text-richblue-500 mb-1">Email Address</label>
                    <input type="email" className="w-full p-2 rounded-md bg-richblue-100 text-richblue-900 border border-richblue-300" />
                  </div>
                  <div>
                    <label className="block text-richblue-500 mb-1">Program of Interest</label>
                    <select className="w-full p-2 rounded-md bg-richblue-100 text-richblue-900 border border-richblue-300">
                      <option>Select a Program</option>
                      <option>Computer Science</option>
                      <option>Business Administration</option>
                      <option>Environmental Science</option>
                      <option>Clinical Psychology</option>
                    </select>
                  </div>
                  <button type="submit" className="w-full bg-richblue-400 text-white py-2 rounded-md hover:bg-richblue-500 transition-all duration-200">
                    Request Information
                  </button>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Specializations Section */}
        {activeTab === 'specializations' && (
          <div>
            <h2 className="text-3xl font-bold text-richblue-700 mb-6">Areas of Specialization</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {specializationAreas.map((area, index) => (
                <div key={index} className="bg-richblue-5 p-6 rounded-lg shadow-md border border-richblue-300 hover:shadow-lg transition">
                  <h3 className="text-xl font-semibold text-richblue-600 mb-3">{area.category}</h3>
                  <ul className="space-y-2">
                    {area.fields.map((field, fieldIndex) => (
                      <li key={fieldIndex} className="flex items-center">
                        <div className="h-2 w-2 bg-richblue-300 rounded-full mr-2"></div>
                        <span className="text-richblue-500">{field}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Admission Requirements Section */}
        {activeTab === 'admissions' && (
          <div>
            <h2 className="text-3xl font-bold text-richblue-700 mb-6">Admission Requirements</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-richblue-5 p-6 rounded-lg shadow-md border border-richblue-300">
                <h3 className="text-xl font-semibold text-richblue-600 mb-4">Basic Requirements</h3>
                <ul className="space-y-4">
                  <li className="flex">
                    <div className="text-richblue-400 mr-3 mt-1 flex-shrink-0">
                      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div>
                      <span className="font-medium text-richblue-600">Master's Degree</span>
                      <p className="text-richblue-500 mt-1">Must be from a recognized institution with good academic standing</p>
                    </div>
                  </li>
                  <li className="flex">
                    <div className="text-richblue-400 mr-3 mt-1 flex-shrink-0">
                      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div>
                      <span className="font-medium text-richblue-600">Professional Experience</span>
                      <p className="text-richblue-500 mt-1">Demonstrated experience in business or related field</p>
                    </div>
                  </li>
                </ul>
              </div>

              <div className="bg-richblue-5 p-6 rounded-lg shadow-md border border-richblue-300">
                <h3 className="text-xl font-semibold text-richblue-600 mb-4">Required Documents</h3>
                <ul className="space-y-4">
                  <li className="flex">
                    <div className="text-richblue-400 mr-3 mt-1 flex-shrink-0">
                      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div>
                      <span className="font-medium text-richblue-600">Statement of Purpose</span>
                      <p className="text-richblue-500 mt-1">Outline your research interests and career goals</p>
                    </div>
                  </li>
                  <li className="flex">
                    <div className="text-richblue-400 mr-3 mt-1 flex-shrink-0">
                      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div>
                      <span className="font-medium text-richblue-600">CV or Resume</span>
                      <p className="text-richblue-500 mt-1">Document your professional and academic achievements</p>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Accreditations Section */}
        {activeTab === 'recognition' && (
          <div>
            <h2 className="text-3xl font-bold text-richblue-700 mb-6">Accreditations & Recognition</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {recognitions.map((recognition, index) => (
                <div key={index} className="bg-richblue-5 p-6 rounded-lg shadow-md border border-richblue-300 flex items-center">
                  <div className="mr-4 bg-richblue-100 p-3 rounded-full">
                    <Award className="h-8 w-8 text-richblue-400" />
                  </div>
                  <div>
                    <h3 className="font-medium text-richblue-700">{recognition.name}</h3>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

       
      </main>
      <Footer />
    </div>
  );
}